import { ReactNode } from 'react';

// 「協会について」共通サイドナビ（about下層6ページで再利用可能な共通コンポーネント）。
// 由来注記: home の 紺帯番号バンド(NavyBand)/金eyebrow+和見出し+真鍮短罫 の文法を
//   「縦組みの固定サイドナビ」へ翻案した新バリエーション。トップに縦ナビは存在しないため新造。
// 文法: 金eyebrow(英字大文字)→和見出し→真鍮短罫→縦リンク列。現在ページは紺塗り+真鍮左罫で強調。
// レスポンシブ: PC=左固定カラム(sticky)。SP=上部横スクロールのピル列に縮退。
// 配線: 今回は overview からのみ使用。他 about ページからも props を差し替えて再利用できる。

interface AboutNavItem {
  label: string;
  href: string;
}

interface AboutSideNavProps {
  items: AboutNavItem[];
  activeHref: string;
  eyebrow?: string;
  heading?: string;
}

export default function AboutSideNav({
  items,
  activeHref,
  eyebrow = 'About Us',
  heading = '協会について',
}: AboutSideNavProps): ReactNode {
  // セクショントップ「協会について」をメニュー先頭項目として合成する。
  // 由来: Yuichi 2026-07-17「ここのサブメニューの協会についてもメニューの一部であるべきじゃないの？」
  //   → 見出しラベル（非リンク）だった協会トップを、クリック可能なメニュー先頭項目に昇格する。
  // ロケール判定: 渡された items の href（/about/* or /en/about/*）から EN を検出する。
  // リンク先は router 実ルート（config.tsx L133 '/about' ／ L362 '/en/about/'）。宙リンクなし・創作なし。
  // ラベルは各ページのパンくずが用いるセクショントップ正式名（JA『協会について』／EN『About Us』）に一致。
  // 現在ページ強調は既存 isActive（item.href === activeHref）がそのまま先頭項目にも適用される。
  const isEN = items.some((item) => item.href.startsWith('/en/'));
  const sectionTop: AboutNavItem = {
    label: isEN ? 'About Us' : '協会について',
    href: isEN ? '/en/about/' : '/about',
  };
  // href の重複を除去（二重表示バグの根治）。入口ページは items 側にもセクショントップ
  //   （'協会について' /about ・'About Us' /en/about）を含めて渡すため、合成した sectionTop と
  //   二重になっていた。末尾スラッシュの有無で同一ルートを取りこぼさないよう正規化して比較する
  //   （sectionTop の EN は router 実ルート '/en/about/'、入口 items は '/en/about' で渡される）。
  //   先勝ち（sectionTop を先頭に維持）でユニーク化する。
  const normalizeHref = (href: string): string =>
    href.length > 1 && href.endsWith('/') ? href.slice(0, -1) : href;
  const seenHrefs = new Set<string>();
  const navItems: AboutNavItem[] = [sectionTop, ...items].filter((item) => {
    const key = normalizeHref(item.href);
    if (seenHrefs.has(key)) return false;
    seenHrefs.add(key);
    return true;
  });

  return (
    <aside className="min-w-0 max-w-full lg:sticky lg:top-[calc(var(--header-h,72px)+24px)]">
      {/* 見出しブロック（金eyebrow→和見出し→真鍮短罫） */}
      <div className="hidden lg:block mb-6">
        <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-[11px] text-[#8f7a56] mb-2">
          {eyebrow}
        </span>
        <h2 className="jba-display font-medium text-[#1a2942] text-xl tracking-[0.04em] leading-tight">
          {heading}
        </h2>
        <div className="w-9 h-px bg-[#a38e65] mt-3"></div>
      </div>

      {/* SP見出し（横スクロール列の上に小さく） */}
      <span className="lg:hidden block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[10px] text-[#8f7a56] mb-2">
        {eyebrow}
      </span>

      <nav
        aria-label={heading}
        className="flex lg:flex-col gap-2 lg:gap-0 overflow-x-auto lg:overflow-visible pb-1 lg:pb-0 -mx-1 px-1 lg:mx-0 lg:px-0"
      >
        {navItems.map((item) => {
          // 末尾スラッシュ差（'/en/about/' と '/en/about'）でも現在ページ強調が外れないよう正規化比較。
          const isActive = normalizeHref(item.href) === normalizeHref(activeHref);
          return (
            <a
              key={item.href}
              href={item.href}
              aria-current={isActive ? 'page' : undefined}
              className={
                isActive
                  ? // 現在ページ: 紺塗り + 真鍮左罫 + 生成り文字
                    "shrink-0 lg:shrink whitespace-nowrap flex items-center gap-2.5 border-l-2 border-[#a38e65] bg-[#1a2942] text-[#f8f6f0] px-4 lg:px-5 py-2.5 lg:py-3.5 text-[13px] lg:text-[15px] tracking-[0.04em] font-['Noto_Serif_JP',serif] lg:border-b lg:border-b-[#a38e65]/20"
                  : // 通常: 生成り地 + 真鍮hover
                    "shrink-0 lg:shrink whitespace-nowrap flex items-center gap-2.5 border-l-2 border-transparent lg:border-b lg:border-b-[#e2ddd2] text-[#4a4638] hover:text-[#1a2942] hover:border-l-[#a38e65]/50 hover:bg-[#f8f6f0] px-4 lg:px-5 py-2.5 lg:py-3.5 text-[13px] lg:text-[15px] tracking-[0.04em] font-['Noto_Serif_JP',serif] transition-colors duration-300"
              }
            >
              <span
                className={
                  isActive
                    ? "text-[#d7b779] text-[10px] leading-none"
                    : "text-[#a38e65] text-[10px] leading-none"
                }
                aria-hidden="true"
              >
                ◆
              </span>
              <span>{item.label}</span>
            </a>
          );
        })}
      </nav>
    </aside>
  );
}
